import React from 'react';
import { Phone, Mail, Share2, MapPin } from 'lucide-react';
import { Button } from './ui/button';
import { getImageUrl } from '../lib/imageUtils';
import { trackLinkClick } from '../lib/analytics';
import { getStallShareUrl } from '../lib/slug';

const StallListItem = ({ stall, onClick, selectedMarket, onGetDirections }) => {
  // Get location based on selected market filter or default to primary location
  const getLocationForMarket = () => {
    // If a specific market is selected and stall has locations array
    if (selectedMarket && selectedMarket !== 'All Markets' && stall.locations?.length > 0) {
      const marketLoc = stall.locations.find(loc => loc.market === selectedMarket);
      if (marketLoc && (marketLoc.lat || marketLoc.lng)) {
        return { 
          lat: marketLoc.lat, 
          lng: marketLoc.lng, 
          stallNumber: marketLoc.stallNumber || stall.stallNumber,
          market: marketLoc.market
        };
      }
    }
    
    // If stall has locations array, use first one with coordinates
    if (stall.locations?.length > 0) {
      const locWithCoords = stall.locations.find(loc => loc.lat && loc.lng);
      if (locWithCoords) {
        return { 
          lat: locWithCoords.lat, 
          lng: locWithCoords.lng, 
          stallNumber: locWithCoords.stallNumber || stall.stallNumber,
          market: locWithCoords.market
        };
      }
    }
    
    // Fallback to legacy single location
    return { 
      lat: stall.location?.lat, 
      lng: stall.location?.lng, 
      stallNumber: stall.stallNumber,
      market: stall.market
    };
  };

  const currentLocation = getLocationForMarket();
  const hasLocation = currentLocation.lat && currentLocation.lng;
  const displayStallNumber = currentLocation.stallNumber || stall.stallNumber;

  const handleGetDirections = (e) => {
    e.stopPropagation();
    if (!hasLocation) {
      alert('Location not available for this stall');
      return;
    }
    trackLinkClick('directions', 'directions', stall._id, stall.stallName, currentLocation.market || stall.market);
    if (onGetDirections) {
      onGetDirections(stall, currentLocation.lat, currentLocation.lng, currentLocation.market || stall.market);
    }
  };

  const handleCall = (e) => {
    e.stopPropagation();
    trackLinkClick('phone', 'contact', stall._id, stall.stallName);
    window.location.href = `tel:${stall.phone}`;
  };

  const handleEmail = (e) => {
    e.stopPropagation();
    trackLinkClick('email', 'contact', stall._id, stall.stallName);
    window.location.href = `mailto:${stall.email}`;
  };

  const handleShare = (e) => {
    e.stopPropagation();
    const shareUrl = getStallShareUrl(stall);
    const shareMessage = `Check out "${stall.stallName}" on WAYO — the vendor platform.`;
    if (navigator.share) {
      navigator.share({
        title: stall.stallName,
        text: shareMessage,
        url: shareUrl,
      });
    } else {
      navigator.clipboard.writeText(shareUrl);
      alert('Link copied to clipboard!');
    }
  };

  return (
    <div 
      className="flex items-center gap-6 p-5 bg-white dark:bg-[#1a1a1a] rounded-lg cursor-pointer hover:shadow-lg transition-all duration-300 border-2 border-gray-100 dark:border-gray-800 hover:border-[#FFD12D]"
      onClick={onClick}
    >
      {/* Logo */}
      <div className="flex-shrink-0">
        <div className="w-20 h-20 rounded-full overflow-hidden border-4 border-[#FFD12D] shadow-md bg-gray-100 dark:bg-gray-700">
          {stall.logo ? (
            <img 
              src={getImageUrl(stall.logo)} 
              alt={`${stall.stallName} logo`}
              className="w-full h-full object-cover"
              loading="lazy"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs">
              Logo
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-1 hover:text-[#FFD12D] transition-colors">
          {stall.stallName}
        </h3>
        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 mb-2">
          <MapPin className="w-4 h-4 text-[#FFD12D]" />
          <span className="font-semibold">Stall {displayStallNumber}</span>
          {!hasLocation && (
            <span className="text-red-400 text-xs">(No GPS)</span>
          )}
        </div>
        <p className="text-gray-600 dark:text-gray-400 text-sm line-clamp-2 mb-3">{stall.about}</p>
        
        {/* Actions */}
        <div className="flex items-center gap-2">
          <Button
            onClick={handleGetDirections}
            size="sm"
            disabled={!hasLocation}
            className={`font-semibold transition-all duration-300 text-xs h-8 ${
              hasLocation 
                ? 'bg-[#FFD12D] hover:bg-[#252525] text-[#252525] hover:text-[#FFD12D]' 
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            Get Directions
          </Button>
          <Button
            onClick={handleCall}
            size="icon"
            variant="ghost"
            className="h-8 w-8 flex-shrink-0 rounded-full hover:bg-[#FFD12D]/20 hover:text-[#252525] transition-all duration-300"
          >
            <Phone className="w-4 h-4" />
          </Button>
          <Button
            onClick={handleEmail}
            size="icon"
            variant="ghost"
            className="h-8 w-8 flex-shrink-0 rounded-full hover:bg-[#FFD12D]/20 hover:text-[#252525] transition-all duration-300"
          >
            <Mail className="w-4 h-4" />
          </Button>
          <Button
            onClick={handleShare}
            size="icon"
            variant="ghost"
            className="h-8 w-8 flex-shrink-0 rounded-full hover:bg-[#FFD12D]/20 hover:text-[#252525] transition-all duration-300"
          >
            <Share2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default StallListItem;
